import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ShoppingCartOutlined,
  DeleteOutlined,
  ShoppingOutlined,
  TagOutlined,
  TruckOutlined,
  SafetyCertificateOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Spin, message } from "antd";

import {
  selectCartItems,
  selectIsCartLoading,
} from "../../redux-toolkit/cart/cart-selector";
import { fetchCart } from "../../redux-toolkit/cart/cart-thunks";
import {
  calculateCartPrice,
  removePerfumeById,
  resetCartState,
  setCartItemsCount,
} from "../../redux-toolkit/cart/cart-slice";
import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { ORDER } from "../../constants/routeConstants";
import "./Cart.css";

const Cart: FC = (): ReactElement => {
  const dispatch  = useDispatch();
  const history   = useHistory();
  const perfumes  = useSelector(selectCartItems);
  const isLoading = useSelector(selectIsCartLoading);
  useSelector(selectUserFromUserState);

  const [isDistributor, setIsDistributor] = useState(false);
  const [perfumeInCart, setPerfumeInCart] = useState<Map<number, number>>(new Map());

  // Per-item editable qty input state
  const [editingQty, setEditingQty] = useState<Record<number, string>>({});
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    setIsDistributor(localStorage.getItem("role") === "3");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    let stored = localStorage.getItem("perfumes");
    if (!stored || stored === "null" || stored === "undefined") stored = "[]";
    let parsed: [number, number][] = [];
    try {
      const temp = JSON.parse(stored);
      parsed = Array.isArray(temp) ? temp : [];
    } catch {
      parsed = [];
      localStorage.setItem("perfumes", "[]");
    }
    const cleaned: [number, number][] = parsed.filter(
      (item) => Array.isArray(item) && typeof item[0] === "number" && typeof item[1] === "number" && !isNaN(item[0])
    );
    if (cleaned.length !== parsed.length) localStorage.setItem("perfumes", JSON.stringify(cleaned));
    const map = new Map<number, number>(cleaned);
    dispatch(fetchCart(Array.from(map.keys())));
    dispatch(setCartItemsCount(map.size));
    map.forEach((qty, id) => setPerfumeInCart((prev) => new Map(prev).set(id, qty)));
  }, [dispatch]);

  /* ── helpers ── */
  const getQty   = (id: number) => perfumeInCart.get(id) || 1;
  const getPrice = (p: any) =>
    isDistributor && p.distributorPrice != null ? Number(p.distributorPrice) : Number(p.price);

  const saveQty = (id: number, next: number, p: any) => {
    const minQty = p.minQuantity || 1;
    const maxQty = p.maxQuantity || 999999;
    if (next < minQty) { message.warning(`Minimum order is ${minQty}`); next = minQty; }
    if (next > maxQty) { message.error(`Maximum quantity is ${maxQty}`);  next = maxQty; }
    const updated = new Map(perfumeInCart).set(id, next);
    setPerfumeInCart(updated);
    localStorage.setItem("perfumes", JSON.stringify(Array.from(updated.entries())));
    dispatch(calculateCartPrice(perfumes.map((pf) => ({
      ...pf, quantity: updated.get(pf.id) || pf.minQuantity || 1,
    }))));
    // Sync input display
    setEditingQty((prev) => ({ ...prev, [id]: String(next) }));
  };

  const changeQty = (id: number, delta: number, p: any) => {
    saveQty(id, getQty(id) + delta, p);
  };

  const deleteFromCart = (id: number) => {
    const updated = new Map(perfumeInCart);
    updated.delete(id);
    localStorage.setItem("perfumes", updated.size ? JSON.stringify(Array.from(updated.entries())) : "[]");
    if (!updated.size) localStorage.removeItem("perfumes");
    setPerfumeInCart(updated);
    dispatch(removePerfumeById(id));
    dispatch(setCartItemsCount(updated.size));
  };

  /* ── Input qty handlers ── */
  const onQtyInputChange = (id: number, val: string) => {
    // Allow free typing — only digits
    if (/^\d*$/.test(val)) setEditingQty((prev) => ({ ...prev, [id]: val }));
  };

  const onQtyInputBlur = (id: number, p: any) => {
    const raw = editingQty[id];
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed > 0) {
      saveQty(id, parsed, p);
    } else {
      // Revert to current qty on bad input
      setEditingQty((prev) => ({ ...prev, [id]: String(getQty(id)) }));
    }
  };

  const onQtyInputKeyDown = (e: React.KeyboardEvent, id: number, p: any) => {
    if (e.key === "Enter") {
      inputRefs.current[id]?.blur();
    }
  };

  /* ── totals ── */
  const subtotal = perfumes.reduce((sum, p) => sum + getPrice(p) * getQty(p.id), 0);
  const deliveryCharge = perfumes.reduce((sum, p) => {
    const ship = Number(p.shipPer ?? 0);
    if (!ship) return sum;
    return sum + Math.max(Math.floor((getPrice(p) * getQty(p.id) * ship) / 100), 1);
  }, 0);
  const total    = subtotal + deliveryCharge;
  const totalQty = Array.from(perfumeInCart.values()).reduce((s, q) => s + q, 0);
  const savings  = perfumes.reduce((sum, p) => {
    const mrp = p.actualPrice != null ? Number(p.actualPrice) : getPrice(p);
    return sum + (mrp - getPrice(p)) * getQty(p.id);
  }, 0);

  if (isLoading) {
    return <div className="ct-page"><div className="ct-center"><Spin size="large" /></div></div>;
  }

  if (perfumes.length === 0) {
    return (
      <div className="ct-page">
        <div className="ct-empty">
          <ShoppingCartOutlined className="ct-empty-icon" />
          <h2 className="ct-empty-title">Your cart is empty!</h2>
          <p className="ct-empty-sub">Add items to it now.</p>
          <button className="ct-empty-btn" onClick={() => history.push("/menu")}>Shop Now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ct-page">
      <div className="ct-layout">

        {/* ── LEFT: cart items ── */}
        <div className="ct-items-col">

          <div className="ct-col-header">
            <ShoppingCartOutlined className="ct-col-icon" />
            <span className="ct-col-title">My Cart</span>
            <span className="ct-col-count">{perfumes.length} item{perfumes.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="ct-assurance">
            <TruckOutlined /> Delivery available · <SafetyCertificateOutlined /> Secure checkout
          </div>

          {perfumes.map((p) => {
            const qty     = getQty(p.id);
            const price   = getPrice(p);
            const mrp     = p.actualPrice != null ? Number(p.actualPrice) : price;
            const hasDisc = mrp > price;
            const discPct = hasDisc ? Math.round(((mrp - price) / mrp) * 100) : 0;
            const minQty  = p.minQuantity || 1;
            const maxQty  = p.maxQuantity || 999999;
            // Input display value: use editingQty if set, else qty
            const inputVal = editingQty[p.id] !== undefined ? editingQty[p.id] : String(qty);

            return (
              <div key={p.id} className="ct-item">

                {/* Image — click navigates to product page */}
                <div
                  className="ct-item-img-wrap"
                  onClick={() => history.push(`/product/${p.id}`)}
                  title="View product"
                >
                  <img
                    src={p.filename || "/no-image.png"}
                    alt={p.perfumeTitle}
                    className="ct-item-img"
                  />
                </div>

                <div className="ct-item-details">
                  <div className="ct-item-top">
                    <div className="ct-item-info">

                      {/* Title — click navigates to product page */}
                      <div
                        className="ct-item-title ct-item-title--link"
                        onClick={() => history.push(`/product/${p.id}`)}
                      >
                        {p.perfumeTitle}
                      </div>
                      <div className="ct-item-brand">{p.brand}</div>

                      <div className="ct-item-price-row">
                        <span className="ct-item-price">₹{price.toLocaleString()}</span>
                        {hasDisc && <span className="ct-item-mrp">₹{mrp.toLocaleString()}</span>}
                        {discPct > 0 && <span className="ct-item-disc">{discPct}% off</span>}
                      </div>

                      {p.minQuantity && p.minQuantity > 1 && (
                        <div className="ct-item-minqty">Min order: {p.minQuantity} units</div>
                      )}
                    </div>

                    <button className="ct-item-delete-btn" onClick={() => deleteFromCart(p.id)} title="Remove">
                      <DeleteOutlined />
                    </button>
                  </div>

                  {/* Qty stepper with editable input */}
                  <div className="ct-item-bottom">
                    <div className="ct-qty-stepper">
                      <button
                        className="ct-qty-btn"
                        onClick={() => changeQty(p.id, -1, p)}
                        disabled={qty <= minQty}
                      >
                        <MinusOutlined />
                      </button>

                      {/* Editable qty input */}
                      <input
                        ref={(el) => { inputRefs.current[p.id] = el; }}
                        className="ct-qty-input"
                        type="text"
                        inputMode="numeric"
                        value={inputVal}
                        onChange={(e) => onQtyInputChange(p.id, e.target.value)}
                        onBlur={() => onQtyInputBlur(p.id, p)}
                        onKeyDown={(e) => onQtyInputKeyDown(e, p.id, p)}
                        onFocus={(e) => e.target.select()}
                      />

                      <button
                        className="ct-qty-btn"
                        onClick={() => changeQty(p.id, 1, p)}
                        disabled={qty >= maxQty}
                      >
                        <PlusOutlined />
                      </button>
                    </div>

                    <span className="ct-item-line-total">₹{(price * qty).toLocaleString()}</span>

                    <button className="ct-item-delete-mob" onClick={() => deleteFromCart(p.id)}>
                      <DeleteOutlined /> Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {savings > 0 && (
            <div className="ct-savings-strip">
              <TagOutlined /> You are saving ₹{savings.toLocaleString()} on this order!
            </div>
          )}
        </div>

        {/* ── RIGHT: price summary ── */}
        <div className="ct-summary-col">
          <div className="ct-summary-card">
            <div className="ct-summary-title">PRICE DETAILS</div>
            <div className="ct-summary-divider" />
            <div className="ct-summary-rows">
              <div className="ct-summary-row">
                <span>Price ({totalQty} item{totalQty !== 1 ? "s" : ""})</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>
              {savings > 0 && (
                <div className="ct-summary-row discount">
                  <span>Discount</span>
                  <span>− ₹{savings.toLocaleString()}</span>
                </div>
              )}
              <div className="ct-summary-row">
                <span>Delivery Charges</span>
                <span className={deliveryCharge === 0 ? "ct-free" : ""}>
                  {deliveryCharge === 0 ? "FREE" : `₹${deliveryCharge.toLocaleString()}`}
                </span>
              </div>
            </div>
            <div className="ct-summary-divider" />
            <div className="ct-summary-total">
              <span>Total Amount</span>
              <span>₹{total.toLocaleString()}</span>
            </div>
            {savings > 0 && (
              <div className="ct-summary-savings">
                You will save ₹{savings.toLocaleString()} on this order
              </div>
            )}
            <button className="ct-checkout-btn" onClick={() => history.push(ORDER)}>
              <ShoppingOutlined /> Checkout
            </button>
            <div className="ct-trust">
              <SafetyCertificateOutlined className="ct-trust-icon" />
              <span>Safe and Secure Payments. 100% Authentic Products.</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE sticky bottom bar ── */}
      <div className="ct-mobile-bar">
        <div className="ct-mobile-total">
          <span className="ct-mobile-total-label">Total</span>
          <span className="ct-mobile-total-val">₹{total.toLocaleString()}</span>
          {savings > 0 && <span className="ct-mobile-savings">Save ₹{savings.toLocaleString()}</span>}
        </div>
        <button className="ct-mobile-checkout" onClick={() => history.push(ORDER)}>
          Checkout →
        </button>
      </div>
    </div>
  );
};

export default Cart;